import {
  booleanAttribute,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NgControl, Validators } from '@angular/forms';
import {
  CloseScrollStrategy,
  ConnectedPosition,
  ConnectedOverlayPositionChange,
  Overlay,
} from '@angular/cdk/overlay';
import { PecAnimations } from '../../animations/pec-animations';
import { DropdownService } from './new-dropdown.service';

@Component({
  selector: 'sigecom-new-dropdown',
  animations: [PecAnimations.showOptions],
  host: { class: 'block' },
  template: `
    <sigecom-input-container
      [label]="label"
      [isRequired]="required"
      [enableRequiredBorder]="enableRequiredBorder"
      (clickOutside)="isOpen && closeDropdown()"
      [isDropdown]="isOpen"
      [ngClass]="{ 'opacity-45': isDisabled }"
      [smallInput]="smallInput"
      [errorMessage]="error || ''"
    >
      <div
        class="relative inline-block text-sigecom-dark-gray w-full"
        [ngClass]="{ 'opacity-45': isDisabled }"
        #dropdownRef
      >
        <div
          class="relative w-full"
          [ngClass]="{ 'pointer-events-none': isDisabled }"
          tabindex="1"
          cdkOverlayOrigin
          #dropdownOrigin="cdkOverlayOrigin"
          (click)="toggleDropdown($event)"
          (keydown.enter)="toggleDropdown($event)"
        >
          <input
            type="text"
            [value]="displayOption"
            readonly
            sigecom-input
            class="w-1/2 pr-10 truncate overflow-hidden "
            [ngClass]="{
              'ring-1 ring-blue-700': isOpen,
            }"
          />
          <fa-icon
            icon="angle-down"
            class="text-xl font-bold absolute right-3 inset-y-1 cursor-pointer transform transition duration-300 ease-in-out"
            [ngClass]="{ ' rotate-180 ': isOpen }"
          />
        </div>
        <ng-template
          cdkConnectedOverlay
          [cdkConnectedOverlayOrigin]="dropdownOrigin"
          [cdkConnectedOverlayOpen]="isOpen"
          [cdkConnectedOverlayPositions]="overlayPositions"
          (positionChange)="onOverlayPositionChange($event)"
          [cdkConnectedOverlayWidth]="dropdownWidth"
          [cdkConnectedOverlayScrollStrategy]="closeOnScrollStrategy"
          [cdkConnectedOverlayViewportMargin]="8"
        >
          <div
            class="z-[100] border border-sigecom-gray bg-white w-full shadow-flat-xl overflow-hidden rounded-lg"
            data-sigecom-dropdown-panel
            (click)="$event.stopPropagation()"
          >
            <div *ngIf="searchble" class="p-2 border-b border-gray-200">
              <input
                #searchInput
                type="text"
                (debouncedInput)="search($event)"
                placeholder="Pesquisar..."
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <ul
              #ul
              id="ul"
              @showOptions
              [ngClass]="{ 'bottom-12': openUp || openUpInternalControl }"
              class="overflow-y-auto max-h-48"
            >
              <li
                *ngIf="!disableDefaultOption"
                class="cursor-pointer px-2 py-1.5 transition-colors duration-200"
                [ngClass]="{
                  'text-white bg-blue-700': selectedOption === null,
                  'text-black hover:bg-blue-900 hover:text-white':
                    selectedOption !== null,
                }"
                (click)="onClick(null)"
                tabindex="0"
                (keydown.enter)="toggleDropdown($event)"
              >
                {{ placeholder }}
              </li>
              <li
                *ngFor="let option of filteredOptions; let i = index"
                class="cursor-pointer px-2 py-1.5 transition-colors duration-200"
                [ngClass]="{
                  'text-white bg-blue-700': areOptionsEqual(
                    option,
                    selectedOption
                  ),
                  'text-black hover:bg-blue-900 hover:text-white':
                    !areOptionsEqual(option, selectedOption),
                }"
                (click)="onClick(option)"
                (keydown.enter)="onClick(option)"
                tabindex="0"
              >
                {{ resolveOptionLabel(option) | formatGender | captalize }}
              </li>
              <li
                *ngIf="filteredOptions.length === 0"
                class="px-2 py-1.5 text-gray-500 text-center"
              >
                Nenhum resultado encontrado
              </li>
            </ul>
            <nav
              class="w-full px-1 py-2 border-t border-gray-200"
              *ngIf="paged"
            >
              <ul class="flex justify-center items-center text-sm">
                <li>
                  <button
                    class="flex items-center justify-end pr-2 w-8 h-8 text-gray-500 hover:text-gray-700 focus:outline-none disabled:opacity-50 disabled:pointer-events-none"
                    (click)="prevPage($event)"
                    [disabled]="_currentPage === 0"
                    aria-label="Página anterior"
                  >
                    <fa-icon icon="chevron-left" />
                  </button>
                </li>
                <li class="flex-1 text-center">
                  <span
                    class="inline-block px-5 text-gray-700 border border-gray-300 rounded-lg bg-gray-100"
                  >
                    {{ paginationLabel }}
                  </span>
                </li>
                <li>
                  <button
                    class="flex items-center justify-start pl-2 w-8 h-8 text-gray-500 hover:text-gray-700 focus:outline-none disabled:opacity-50 disabled:pointer-events-none"
                    (click)="nextPage($event)"
                    [disabled]="_currentPage >= _totalPages - 1"
                    aria-label="Próxima página"
                  >
                    <fa-icon icon="chevron-right" />
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </ng-template>
      </div>
    </sigecom-input-container>
  `,
})
export class NewDropdownComponent<T>
  implements ControlValueAccessor, OnChanges, OnInit, OnDestroy
{
  @ViewChild('dropdownRef', { static: true })
  dropdownRef!: ElementRef<HTMLDivElement>;
  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;
  @Input({ transform: booleanAttribute }) smallInput = false;
  @Input() label?: string;
  @Input() optionLabel: keyof T | ((option: T) => string) = 'label' as keyof T;
  @Input() options: T[] = [];
  @Input() optionIdentify: keyof T | null = null;
  @Input({ transform: booleanAttribute }) disableDefaultOption = false;
  @Input() placeholder = 'Selecione...';
  @Input() error: string | null = null;
  @Input() directionBasedOnParentSelector: string | null = null;
  @Input() openUp = false;
  @Input({ transform: booleanAttribute }) required = false;
  @Input() combineLabel?: (keyof T)[];
  @Input({ alias: 'disabled', transform: booleanAttribute })
  set disabledInput(value: boolean) {
    this.isDisabled = value;
  }
  @Input() enableRequiredBorder = false;
  private ngControl = inject(NgControl, { optional: true });

  @Output() changeOption = new EventEmitter<T | null>();

  // Paginação
  @Input({ transform: booleanAttribute }) paged = false;
  _totalPages!: number;
  @Input()
  set totalPages(value: number) {
    if (this.paged && value === undefined) {
      throw new Error('"totalPages" é obrigatório quando "paged" é "true"');
    }
    this._totalPages = value;
  }
  get totalPages(): number {
    return this._totalPages;
  }

  get dropdownWidth(): number {
    return this.dropdownRef?.nativeElement.offsetWidth ?? 0;
  }

  _currentPage!: number;
  @Input()
  set currentPage(value: number) {
    if (this.paged && value === undefined) {
      throw new Error('"currentPage" é obrigatório quando "paged" é "true"');
    }
    this._currentPage = value;
  }
  get currentPage(): number {
    return this._currentPage;
  }

  @Output() pageChange = new EventEmitter<number>();

  // Pesquisa
  @Input({ transform: booleanAttribute }) searchble = false;
  @Output() searchChange = new EventEmitter();

  isOpen = false;
  isOverlayAbove = false;
  openUpInternalControl = false;
  selectedOption: T | null = null;
  filteredOptions: T[] = [];
  isDisabled = false;
  searchText = '';
  private rawValue: any = null;

  dropdownService = inject(DropdownService);
  private readonly overlay = inject(Overlay);
  readonly closeOnScrollStrategy: CloseScrollStrategy =
    this.overlay.scrollStrategies.close();
  readonly overlayPositions: ConnectedPosition[] = [
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
    },
    {
      originX: 'start',
      originY: 'top',
      overlayX: 'start',
      overlayY: 'bottom',
    },
  ];

  // REMOVI selectedOptTemp e adicionei esta variável para mais clareza
  private _lastValidSelectedOption: T | null = null;

  @Input()
  set lastValidSelectedOption(value: T | null) {
    if (
      !value ||
      (typeof value === 'object' && Object.keys(value).length === 0)
    ) {
      this._lastValidSelectedOption = null;
      this.selectedOption = null;
      this.updateFilteredOptions();
      return;
    }

    this._lastValidSelectedOption = value;

    const found = this.options.find((opt) =>
      this.optionIdentify
        ? opt[this.optionIdentify] === value[this.optionIdentify]
        : this.areOptionsEqual(opt, value),
    );

    this.selectedOption = found ?? value;
    this.updateFilteredOptions();
  }

  get lastValidSelectedOption(): T | null {
    return this._lastValidSelectedOption;
  }

  protected onTouched?: () => {};
  protected onChange?: (value: T | T[keyof T] | null) => {};

  constructor() {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
    this.dropdownService.onCloseAllDropdowns.subscribe(() => {
      this.isOpen = false;
    });
  }

  private readonly onDocumentScroll = (event: Event): void => {
    const target = event.target;
    if (
      target instanceof Element &&
      target.closest('[data-sigecom-dropdown-panel]')
    ) {
      return;
    }

    this.closeDropdown(false);
  };

  get paginationLabel(): string {
    let currentPage = this._currentPage + 1;
    return currentPage + '/' + this._totalPages;
  }

  protected resolveOptionLabel(option: T | null): string {
    if (!option) return this.placeholder;

    if (this.combineLabel?.length) {
      const values = this.combineLabel
        .map((key) => option[key])
        .filter((v) => v !== null && v !== undefined && v !== '');

      const primary =
        typeof this.optionLabel === 'function'
          ? this.optionLabel(option)
          : String(option[this.optionLabel as keyof T] ?? '');

      return [primary, ...values].join(' - ');
    }

    if (typeof this.optionLabel === 'function') {
      return this.optionLabel(option);
    }

    if (typeof this.optionLabel === 'string') {
      return String(option[this.optionLabel] ?? this.placeholder);
    }

    return String(option);
  }

  get displayOption(): string {
    return this.resolveOptionLabel(this.selectedOption);
  }

  private syncSelectedOption(): void {
    if (this._lastValidSelectedOption) {
      this.selectedOption =
        (this.options || []).find((option) =>
          this.areOptionsEqual(option, this._lastValidSelectedOption),
        ) ?? this._lastValidSelectedOption;
      return;
    }

    const val = this.ngControl ? this.ngControl.value : this.rawValue;

    if (val === null || val === undefined) {
      if (this._lastValidSelectedOption) {
        this.selectedOption = this._lastValidSelectedOption;
        return;
      }

      this.selectedOption = null;
      this._lastValidSelectedOption = null;
      return;
    }

    if (this.optionIdentify) {
      const valToMatch =
        typeof val === 'object' && val !== null
          ? val[this.optionIdentify]
          : val;
      const found = (this.options || []).find(
        (opt) => opt && opt[this.optionIdentify!] === valToMatch,
      );

      if (found) {
        this.selectedOption = found;
        this._lastValidSelectedOption = found;
      } else if (
        this._lastValidSelectedOption &&
        (typeof this._lastValidSelectedOption === 'object' &&
        this._lastValidSelectedOption !== null
          ? this._lastValidSelectedOption[this.optionIdentify!] === valToMatch
          : this._lastValidSelectedOption === valToMatch)
      ) {
        this.selectedOption = this._lastValidSelectedOption;
      } else if (typeof val === 'object' && val !== null) {
        this.selectedOption = val;
        this._lastValidSelectedOption = val;
      } else if (
        this._lastValidSelectedOption?.[this.optionIdentify] === valToMatch
      ) {
        // Keep the explicitly supplied selection when it is not part of the
        // current page of options.
        this.selectedOption = this._lastValidSelectedOption;
      } else {
        this.selectedOption = this._lastValidSelectedOption ?? null;
      }
    } else {
      const found = (this.options || []).find((opt) =>
        this.areOptionsEqual(opt, val),
      );

      if (found) {
        this.selectedOption = found;
        this._lastValidSelectedOption = found;
      } else if (
        this._lastValidSelectedOption &&
        this.areOptionsEqual(this._lastValidSelectedOption, val)
      ) {
        this.selectedOption = this._lastValidSelectedOption;
      } else {
        this.selectedOption = this._lastValidSelectedOption ?? val;
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['options']) {
      this.updateFilteredOptions();

      // Dropdowns used without a form control are driven exclusively by
      // `lastValidSelectedOption`. In that case, syncing from `rawValue`
      // (which is null) would immediately clear the selected option set by
      // the input setter.
      if (
        this.ngControl ||
        (this.rawValue !== null && this.rawValue !== undefined)
      ) {
        this.syncSelectedOption();
      }
    }

    if (changes['lastValidSelectedOption']) {
      // The input setter already resolves and applies the selected option.
      // Do not re-sync here: an unbound dropdown has no ControlValueAccessor
      // value to restore and would lose its selected option.
      this.updateFilteredOptions();
    }
  }

  ngOnInit(): void {
    document.addEventListener('scroll', this.onDocumentScroll, true);
    this.updateFilteredOptions();
    const control = this.ngControl?.control;

    if (!control) {
      return;
    }
    this.required = control.hasValidator(Validators.required) ?? false;

    control.statusChanges?.subscribe((status) => {
      if (status === 'INVALID') {
        this.isOpen = false;
      }
    });

    control.statusChanges.subscribe(() => {
      this.required = control.hasValidator(Validators.required);
    });
  }

  ngOnDestroy(): void {
    document.removeEventListener('scroll', this.onDocumentScroll, true);
  }

  protected areOptionsEqual(option1: T | null, option2: T | null): boolean {
    if (!option1 || !option2) {
      return option1 === option2;
    }
    if (this.optionIdentify) {
      return option1[this.optionIdentify] === option2[this.optionIdentify];
    }
    return option1 === option2;
  }

  private updateFilteredOptions(): void {
    this.filteredOptions = [...(this.options || [])];

    if (this.paged && this.lastValidSelectedOption) {
      const isSelectedOptionInPage = this.filteredOptions.some((opt) =>
        this.areOptionsEqual(opt, this.lastValidSelectedOption),
      );

      if (!isSelectedOptionInPage) {
        this.filteredOptions.unshift(this.lastValidSelectedOption);
      }
    }
  }

  protected toggleDropdown(ev: Event): void {
    ev.stopPropagation();

    if (this.isDisabled) return;

    const next = !this.isOpen;

    if (next) {
      this.dropdownService.closeAll();
    }

    queueMicrotask(() => {
      this.isOpen = next;

      if (this.isOpen) {
        this.checkDropdownDirection();
      }

      if (this.searchText !== '') {
        this.searchChange.emit('');
      }
    });
  }

  public closeDropdown(markAsTouched = true): void {
    this.isOpen = false;
    this.clearSearch();

    if (markAsTouched) {
      this.onTouched?.();
    }
  }

  private clearSearch(): void {
    if (this.searchText === '') return;

    this.searchText = '';
    if (this.searchInput) {
      this.searchInput.nativeElement.value = '';
    }
    this.searchChange.emit('');
  }

  protected onClick(option: T | null, event?: Event): void {
    if (this.isDisabled) return;
    event?.stopPropagation();

    this.selectedOption = option;
    this.lastValidSelectedOption = option;
    this.changeOption.emit(option);

    if (this.ngControl) {
      if (!option) {
        this.onChange?.(null);
        this.clearSearch();
        queueMicrotask(() => {
          this.isOpen = false;
        });
        return;
      }
      const optionIdentifyValue = this.optionIdentify
        ? option[this.optionIdentify]
        : null;
      this.onChange?.(this.optionIdentify ? optionIdentifyValue : option);
    } else {
      this.onChange?.(option);
    }
    this.clearSearch();
    queueMicrotask(() => {
      this.isOpen = false;
    });
  }

  private checkDropdownDirection(): void {
    this.checkDropdownDirectionBasedScreen();
  }

  private checkDropdownDirectionBasedScreen(): void {
    const dropdownRect = this.dropdownRef.nativeElement.getBoundingClientRect();
    const optionsHeight = 200;
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - dropdownRect.bottom;
    const spaceAbove = dropdownRect.top;
    this.openUpInternalControl =
      spaceBelow < optionsHeight && spaceAbove > spaceBelow;
  }

  protected search(text: string): void {
    this.searchChange.emit(text);
    this.searchText = text;
  }

  onOverlayPositionChange(event: ConnectedOverlayPositionChange): void {
    this.isOverlayAbove = event.connectionPair.originY === 'top';
  }

  writeValue(value: any): void {
    if (value === null || value === undefined) {
      this.reset();
      return;
    }

    this.rawValue = value;
    this.syncSelectedOption();
  }

  protected prevPage(event: Event) {
    event.stopPropagation();
    if (this._currentPage > 0) {
      this.pageChange.emit(this._currentPage - 1);
    }
  }

  protected nextPage(event: Event) {
    event.stopPropagation();
    if (this._currentPage < this._totalPages - 1) {
      this.pageChange.emit(this._currentPage + 1);
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  reset(): void {
    this.selectedOption = null;
    this.lastValidSelectedOption = null;
    this.rawValue = null;
    this.filteredOptions = [...this.options];
  }
}
